import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * Alert coverage (SOUND-847) — internal-only, read-only.
 *
 *   GET /alerts?from&to&grain=merchant|mid
 *     → { data: { asOf, from, to, grain, totals, rows: [row] } }
 *   GET /alerts/series?key&from&to
 *     → { data: { row, days: [{ date, ethoca, cdrn, rdr, tc40, chargebacks, chargebacksUnmatched }] } }
 *
 * Same thin makeApiRequest pattern as useRouting. Mounted only behind
 * <RequireRole role="internal">; the API enforces internal-only too.
 */
function useGet(url, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();

  const fetchIt = useCallback(async () => {
    if (!enabled || !url || !headerCompanyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest("get", url, auth.user, headerCompanyId);
      setData(res.data?.data ?? null);
    } catch (err) {
      setData(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, url, headerCompanyId, auth?.user]);

  useEffect(() => {
    fetchIt();
  }, [fetchIt]);

  return { data, loading, error, refetch: fetchIt };
}

/** One grain of the coverage list. Pass `enabled: false` to defer (e.g. MID grain until needed). */
export function useAlerts({ from, to, grain = "merchant", enabled = true } = {}) {
  const { data, ...rest } = useGet(apiRoutes.alerts({ from, to, grain }), enabled);
  return {
    asOf: data?.asOf ?? null,
    from: data?.from ?? null,
    to: data?.to ?? null,
    totals: data?.totals ?? null,
    rows: Array.isArray(data?.rows) ? data.rows : [],
    ...rest,
  };
}

/** Daily series for one row (by its key) over a range. */
export function useAlertSeries({ key, from, to }) {
  const { data, ...rest } = useGet(
    key ? apiRoutes.alertSeries({ key, from, to }) : null,
    Boolean(key)
  );
  return {
    row: data?.row ?? null,
    days: Array.isArray(data?.days) ? data.days : [],
    ...rest,
  };
}
